import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Eye, Printer, Clock, AlertTriangle, BarChart3, PhoneCall, HelpCircle, ArrowRight, Bell, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Hint = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: 'rgba(235, 110, 31, 0.04)', border: '1px solid rgba(235, 110, 31, 0.12)', padding: '0.65rem 0.85rem', fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '0.5rem' }}>
    <HelpCircle size={15} style={{ color: 'var(--astros-orange)', flexShrink: 0, marginTop: 2 }} />
    <span>{text}</span>
  </div>
);

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  return 'Evening';
};

const getDayString = () => {
  const now = new Date();
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[now.getDay()];
};

const getTimeString = () => {
  return new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const StatsView = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [newPeople, setNewPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(getTimeString());
  const [amigosCount, setAmigosCount] = useState(0);
  const [amigosNotifs, setAmigosNotifs] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => setTime(getTimeString()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, recentRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/stats`, { headers }),
          axios.get(`${API}/submissions/recent`, { headers }),
          user?.role === 'manager' ? axios.get(`${API}/analytics/overview`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        ]);
        setStats(statsRes.data);
        setRecent(recentRes.data || []);
        setNewPeople(analyticsRes.data?.new_people || []);

        // Fetch Amigos cross-brand alerts
        try {
          const [countRes, notifsRes] = await Promise.all([
            axios.get(`${API}/cross-brand/amigos/unread-count`, { headers }),
            axios.get(`${API}/cross-brand/amigos/notifications`, { headers }),
          ]);
          setAmigosCount(countRes.data?.count || 0);
          setAmigosNotifs(Array.isArray(notifsRes.data) ? notifsRes.data.filter(n => !n.viewed) : []);
        } catch { /* Amigos not connected — silent */ }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [user]);

  // Listen for real-time Amigos push via WebSocket
  useEffect(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsBase = process.env.REACT_APP_BACKEND_URL?.replace(/^https?/, wsProtocol);
    if (!wsBase) return;
    let ws;
    try {
      ws = new WebSocket(`${wsBase}/ws/notifications`);
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'amigos_notification_count' && (!msg.recipient_email || msg.recipient_email === user?.email)) {
            setAmigosCount(msg.count || 0);
          }
        } catch { /* ignore parse errors */ }
      };
    } catch { /* ws not available */ }
    return () => { if (ws) ws.close(); };
  }, [user]);

  const getStatus = (sub) => {
    if (sub.printed) return 'printed';
    if (sub.viewed) return 'viewed';
    return 'new';
  };

  const statusBadge = (sub) => {
    const s = getStatus(sub);
    if (s === 'new') return <span className="badge-new">NEW</span>;
    if (s === 'viewed') return <span className="badge-viewed">VIEWED</span>;
    if (s === 'printed') return <span className="badge-printed">PRINTED</span>;
    return null;
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)', fontSize: '1.2rem' }}>Loading dashboard...</div>;

  const newCount = stats?.new_unviewed || 0;
  const greeting = getGreeting();
  const dayName = getDayString();

  return (
    <div data-testid="stats-view">
      {/* ── Personalized Greeting ────────────────────────── */}
      <div style={{ marginBottom: '2rem', padding: '2rem', background: 'linear-gradient(135deg, rgba(0, 40, 104, 0.2), rgba(235, 110, 31, 0.08))', border: '1px solid rgba(235, 110, 31, 0.15)' }} data-testid="greeting-banner">
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'white', letterSpacing: '0.03em', marginBottom: '0.35rem', lineHeight: 1.2 }}>
          Good {greeting}, Mr. Cruz.
        </h1>
        <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
          Today is <strong style={{ color: 'white' }}>{dayName}</strong> and it's currently <strong style={{ color: 'var(--astros-orange)' }}>{time}</strong>
        </p>
      </div>

      {/* ── New Submissions Alert ────────────────────────── */}
      {newCount > 0 && (
        <Link to="/dashboard/submissions" data-testid="new-alert-banner" style={{
          display: 'block', padding: '1.5rem', textDecoration: 'none',
          background: 'rgba(191, 10, 48, 0.10)', border: '2px solid rgba(191, 10, 48, 0.4)',
          marginBottom: '1.5rem', transition: 'all 0.3s',
          animation: 'pulse-subtle 2s infinite',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <Bell size={28} style={{ color: 'var(--texas-red)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'white', letterSpacing: '0.04em' }}>
                {newCount === 1
                  ? `THERE IS A NEW FORM SUBMISSION`
                  : `THERE ARE ${newCount} NEW FORM SUBMISSIONS`}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '3.5rem', color: 'var(--texas-red)', lineHeight: 1 }}>
              {newCount}
            </div>
          </div>
          {/* List the names */}
          {newPeople.length > 0 && (
            <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(191, 10, 48, 0.2)' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {newCount === 1 ? 'From:' : 'From these people:'}
              </div>
              {newPeople.slice(0, 5).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem' }}>
                  <User size={14} style={{ color: 'var(--astros-orange)', flexShrink: 0 }} />
                  <span style={{ fontSize: '1rem', color: 'white', fontWeight: 700 }}>
                    {p.full_name || 'Unknown'}
                  </span>
                  {p.defendant_name && p.defendant_name !== p.full_name && (
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      (for {p.defendant_name})
                    </span>
                  )}
                  {p.charge && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      — {p.charge}
                    </span>
                  )}
                </div>
              ))}
              {newPeople.length > 5 && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                  ...and {newPeople.length - 5} more
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.75rem', fontSize: '0.9rem', color: 'var(--astros-orange)', fontWeight: 700 }}>
            Click here to review them now <ArrowRight size={16} />
          </div>
        </Link>
      )}

      {newCount === 0 && amigosCount === 0 && (
        <div style={{ padding: '1.5rem', background: 'rgba(46, 229, 157, 0.06)', border: '1px solid rgba(46, 229, 157, 0.15)', marginBottom: '1.5rem' }} data-testid="no-new-banner">
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--success)', letterSpacing: '0.04em' }}>
            ALL CAUGHT UP!
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.25rem 0 0' }}>
            You have no new submissions waiting from either location. When someone fills out a form, it will appear here.
          </p>
        </div>
      )}

      {/* ── Amigos Cross-Brand Alert ─────────────────────── */}
      {amigosCount > 0 && (
        <a href="https://amigosbailbonds.com/dashboard"
          target="_blank" rel="noopener noreferrer"
          data-testid="amigos-alert-banner"
          style={{
            display: 'block', padding: '1.5rem', textDecoration: 'none',
            background: 'rgba(243, 156, 18, 0.10)', border: '2px solid rgba(243, 156, 18, 0.45)',
            marginBottom: '1.5rem', cursor: 'pointer',
            animation: 'pulse-subtle 2s infinite',
            transition: 'background 0.2s ease, border-color 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(243, 156, 18, 0.18)'; e.currentTarget.style.borderColor = 'rgba(243, 156, 18, 0.65)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(243, 156, 18, 0.10)'; e.currentTarget.style.borderColor = 'rgba(243, 156, 18, 0.45)'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <Bell size={28} style={{ color: '#F39C12', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.85rem', color: '#F39C12', letterSpacing: '0.08em', marginBottom: '0.25rem' }}>
                AMIGO'S BAIL BONDS
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'white', letterSpacing: '0.04em' }}>
                {amigosCount === 1
                  ? 'THERE IS 1 NEW ALERT FROM AMIGOS'
                  : `THERE ARE ${amigosCount} NEW ALERTS FROM AMIGOS`}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '3.5rem', color: '#F39C12', lineHeight: 1 }}>
              {amigosCount}
            </div>
          </div>
          {/* List Amigos notifications */}
          {amigosNotifs.length > 0 && (
            <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(243, 156, 18, 0.2)' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Recent alerts:
              </div>
              {amigosNotifs.slice(0, 5).map((n, i) => (
                <div key={n.id || i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem' }}>
                  <AlertTriangle size={14} style={{ color: '#F39C12', flexShrink: 0 }} />
                  <span style={{ fontSize: '1rem', color: 'white', fontWeight: 700 }}>
                    {n.title || n.message || 'New notification'}
                  </span>
                  {n.created_at && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      — {new Date(n.created_at).toLocaleString()}
                    </span>
                  )}
                </div>
              ))}
              {amigosNotifs.length > 5 && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                  ...and {amigosNotifs.length - 5} more
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.75rem', fontSize: '0.9rem', color: '#F39C12', fontWeight: 700 }}>
            Click here to view on Amigo's Dashboard <ArrowRight size={16} />
          </div>
        </a>
      )}

      {/* ── Stats Cards ──────────────────────────────────── */}
      <Hint text="These boxes show you the big picture: how many total forms have come in, how many are brand new, how many calls your website generated, and how many people visited your site." />
      <div className="dash-stats" data-testid="stats-grid">
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><FileText size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> TOTAL FORMS</div>
          <div className="dash-stat-card__value">{stats?.total_submissions || 0}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>All forms ever submitted</div>
        </div>
        <div className="dash-stat-card" style={{ borderColor: newCount > 0 ? 'rgba(191, 10, 48, 0.5)' : undefined }}>
          <div className="dash-stat-card__label"><Clock size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> NEW / WAITING</div>
          <div className={`dash-stat-card__value ${newCount > 0 ? 'dash-stat-card__value--alert' : ''}`}>{newCount}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Not yet opened by you</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><PhoneCall size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> PHONE CALLS</div>
          <div className="dash-stat-card__value">{stats?.call_clicks || 0}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>People tapped "Call Now"</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><BarChart3 size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> SITE VISITS</div>
          <div className="dash-stat-card__value">{stats?.page_views || 0}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>People who visited your website</div>
        </div>
      </div>

      {/* ── Quick Guide ──────────────────────────────────── */}
      <div style={{ padding: '1.25rem', background: 'rgba(235, 110, 31, 0.03)', border: '1px solid rgba(235, 110, 31, 0.10)', marginBottom: '1.5rem', marginTop: '1.5rem' }} data-testid="quick-guide">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--astros-orange)', letterSpacing: '0.04em', marginBottom: '0.75rem' }}>
          HOW TO USE THIS DASHBOARD
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
          {[
            { icon: <FileText size={18} />, title: 'SUBMISSIONS', desc: 'Click "SUBMISSIONS" in the sidebar to see all forms. Click any row to expand it and print the PDFs.' },
            { icon: <BarChart3 size={18} />, title: 'ANALYTICS', desc: 'Click "ANALYTICS" to see your business report — charges, bond amounts, where clients come from.' },
            { icon: <Eye size={18} />, title: 'NEW vs VIEWED', desc: 'New forms glow red. Once you open them, they turn "VIEWED". After printing, they say "PRINTED".' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
              <div style={{ color: 'var(--astros-orange)', flexShrink: 0, marginTop: 2 }}>{item.icon}</div>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', marginBottom: '0.15rem' }}>{item.title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Recent Submissions ───────────────────────────── */}
      <div style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'white', letterSpacing: '0.05em' }}>RECENT SUBMISSIONS</h2>
          <Link to="/dashboard/submissions" className="btn-outline" data-testid="view-all-submissions">
            VIEW ALL <FileText size={14} />
          </Link>
        </div>
        <Hint text="This table shows the most recent forms people submitted. NEW means you haven't looked at it yet. Click 'VIEW ALL' to see everything and print PDFs." />

        {recent.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border-dim)', color: 'var(--text-muted)', fontSize: '1.1rem' }}>
            No submissions yet. When someone fills out a form on your website, it will show up here.
          </div>
        ) : (
          <div className="dash-table-wrap">
          <table className="dash-table" data-testid="recent-submissions-table">
            <thead>
              <tr>
                <th>STATUS</th>
                <th>NAME</th>
                <th>DATE</th>
                <th>SSN LAST 4</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((sub, i) => (
                <tr key={sub.id || i}>
                  <td>{statusBadge(sub)}</td>
                  <td style={{ fontWeight: 700, color: 'white', fontSize: '1rem' }}>
                    {sub.defendant_name || sub.full_name || 'N/A'}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}>
                    {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>***{sub.ssn_last4 || '????'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsView;
